import { useAppSSE } from 'contexts/AppSSEContext';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  addDeviceFromEvent,
  applyDeviceUpdatesFromEvent,
  removeDeviceFromEvent,
} from 'services/devices/devices.service';
import {
  addGroupFromEvent,
  removeGroupFromEvent,
  updateGroupFromEvent,
} from 'services/devices/groups/groups.service';
import {
  addSystemFromEvent,
  removeSystemFromEvent,
  updateSystemFromEvent,
} from 'services/integrations/integrations.service';

const notifySseActivity =
  process.env.NODE_ENV === 'development'
    ? require('components/DevRenderOverlay/sseActivityHelper').notifySseActivity
    : () => {};

export default function useIntegrationEvents({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const { subscribe } = useAppSSE();

  const handlers = useMemo(
    () => ({
      'device-update-user': (eventData) => {
        notifySseActivity('device-update-user');
        applyDeviceUpdatesFromEvent(dispatch, eventData);
      },
      'device-update': (eventData) => {
        notifySseActivity('device-update');
        applyDeviceUpdatesFromEvent(dispatch, eventData);
      },
      'device-add': (eventData) => {
        notifySseActivity('device-add');
        addDeviceFromEvent(dispatch, eventData);
      },
      'device-remove': (eventData) => {
        notifySseActivity('device-remove');
        removeDeviceFromEvent(dispatch, eventData);
      },
      'group-update': (eventData) => {
        notifySseActivity('group-update');
        updateGroupFromEvent(dispatch, eventData);
      },
      'group-add': (eventData) => {
        notifySseActivity('group-add');
        addGroupFromEvent(dispatch, eventData);
      },
      'group-remove': (eventData) => {
        notifySseActivity('group-remove');
        removeGroupFromEvent(dispatch, eventData.id);
      },
      'system-update': (eventData) => {
        notifySseActivity('system-update');
        updateSystemFromEvent(dispatch, eventData);
      },
      'system-add': (eventData) => {
        notifySseActivity('system-add');
        addSystemFromEvent(dispatch, {
          system: eventData.system,
          allDeviceTypes: eventData.device_types.allDeviceTypes,
        });
      },
      'system-remove': (eventData) => {
        notifySseActivity('system-remove');
        removeSystemFromEvent(dispatch, eventData.id);
      },
    }),
    [dispatch],
  );

  useEffect(() => {
    if (!enabled) return;

    const unsubs = Object.entries(handlers).map(([eventType, handler]) =>
      subscribe(eventType, handler),
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [enabled, subscribe, handlers]);
}
